/**
 * ManagePermissions
 *
 * Top-level screen to manage application permissions either by Roles or by Users.
 * Provides an edit mode that enables toggling permissions, tracks changes to build
 * an update payload, and persists via `updateRolePermissions`. Also shows a last
 * updated timestamp and a simple legend for R/W/D.
 */
import React, { useState } from "react";
import BreadcrumbComponent from "../../common/components/breadcrumb-component/breadcrumb-component";
import RolesAndPermissionsdAddPopup from "./roles-and-permissions-add-popup/roles-and-permissions-add-popup";
import UserList from "./user-list/user-list";
import { usePermissions } from "../../common/hooks/usePermissions";
import MODULES from "../../constants/modules";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  adminHeaders,
  deptOwnersHeaders,
  eltHeaders,
} from "./manage-permission.utils";
import "./manage-permissions.scss";

dayjs.extend(utc);
dayjs.extend(timezone);

const ManagePermissions: React.FC = () => {
  const breadcrumbLink = [
    {
      label: "Manage Permissions",
    },
  ];
  const [crumbMappingOption] = useState<{ label: string }[]>(breadcrumbLink);
  const [activeTab, setActiveTab] = useState<
    "admins" | "elt" | "department_owners"
  >("admins");
  const [isOpenAddPopup, setIsOpenAddPopup] = useState(false);
  const { canWrite } = usePermissions(MODULES.ROLE_MANAGEMENT);

  const handleCloseAddPopup = () => {
    setIsOpenAddPopup(false);
  };

  return (
    <>
      <div className="manage-permissions">
        <div className="manage-permissions-header">
          <BreadcrumbComponent
            breadcrumbLink={crumbMappingOption}
            showBackArrow={true}
          />
        </div>
        <div className="manage-permissions-content">
          <div className="manage-permissions-content-tab">
            <div className="manage-permissions-content-tab-row">
              <div className="manage-permissions-content-tab-container">
                <button
                  className={`manage-permissions-content-tab-btn ${
                    activeTab === "admins"
                      ? "manage-permissions-content-tab-active"
                      : ""
                  }`}
                  onClick={() => setActiveTab("admins")}
                >
                  Admins
                </button>
                <button
                  className={`manage-permissions-content-tab-btn ${
                    activeTab === "elt"
                      ? "manage-permissions-content-tab-active"
                      : ""
                  }`}
                  onClick={() => setActiveTab("elt")}
                >
                  ELT
                </button>
                <button
                  className={`manage-permissions-content-tab-btn ${
                    activeTab === "department_owners"
                      ? "manage-permissions-content-tab-active"
                      : ""
                  }`}
                  onClick={() => setActiveTab("department_owners")}
                >
                  Dept. Owners
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="manage-permissions-table">
          {activeTab === "admins" && (
            <UserList
              canWrite={canWrite}
              headers={adminHeaders}
              activeTab={activeTab}
            />
          )}
          {activeTab === "elt" && (
            <UserList
              canWrite={canWrite}
              headers={eltHeaders}
              activeTab={activeTab}
            />
          )}
          {activeTab === "department_owners" && (
            <UserList
              canWrite={canWrite}
              headers={deptOwnersHeaders}
              activeTab={activeTab}
            />
          )}
        </div>
      </div>
      <RolesAndPermissionsdAddPopup
        isOpen={isOpenAddPopup}
        onClose={handleCloseAddPopup}
      />
    </>
  );
};

export default ManagePermissions;
